// Path: ./src/app/components/MuiConfirmationModal/index.tsx
import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { Alert, Box, SxProps, Theme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import { CancelButton, ConfirmButton } from "./styled";

interface Props {
  open: boolean;
  /**
   * Handles the cancel action for a confirmation modal.
   * This function is typically called when the user cancels the modal operation.
   */
  handleCancel?: () => void;
  /**
   * Handles the confirm action for a confirmation modal.
   * This function is typically called when the user confirms the modal operation.
   */
  handleConfirm?: () => void;
  title?: string;
  description?: string | React.ReactNode;
  /**
   * Custom color for the confirm button. Defaults to red if not provided.
   */
  confirmButtonColor?: string;
  /**
   * Whether to show the confirm button. Defaults to true.
   */
  showConfirmButton?: boolean;
  /**
   * Optional warning message to display in an alert box
   */
  warningMessage?: string | React.ReactNode;
  /**
   * Warning alert severity. Defaults to 'warning' if not provided.
   */
  warningSeverity?: "error" | "warning" | "info" | "success";
  /**
   * Optional sx prop to style the Dialog component (e.g., for zIndex customization)
   */
  sx?: SxProps<Theme>;
  /**
   * Optional test id base for component testing.
   */
  dataTestId?: string;
  /**
   * Optional content shown below the description, such as an input the
   * confirm action uses.
   */
  children?: React.ReactNode;
}
export function MuiConfirmationModal({
  open,
  handleCancel,
  handleConfirm,
  title,
  description,
  confirmButtonColor,
  showConfirmButton = true,
  warningMessage,
  warningSeverity = "warning",
  sx,
  dataTestId = "muiconfirmationmodal-test-id",
  children,
}: Props) {
  const { t } = useTranslation();

  return (
    <div data-testid={dataTestId}>
      <Dialog
        data-testid={`${dataTestId}-dialog`}
        open={open}
        onClose={handleCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        sx={[
          {
            "& .MuiDialog-paper": {
              mx: { xs: 2, sm: "auto" },
              width: { xs: "100%", sm: "auto" },
              maxWidth: { xs: "calc(100% - 32px)", sm: 600 },
            },
          },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
      >
        <DialogTitle
          id="alert-dialog-title"
          data-testid={`${dataTestId}-title`}
        >
          {title ? title : ""}
        </DialogTitle>
        <DialogContent data-testid={`${dataTestId}-content`}>
          {warningMessage && (
            <Box sx={{ mb: 2 }} data-testid={`${dataTestId}-warning-wrapper`}>
              <Alert
                severity={warningSeverity}
                data-testid={`${dataTestId}-warning`}
              >
                {warningMessage}
              </Alert>
            </Box>
          )}
          {typeof description === "string" ? (
            <DialogContentText
              id="alert-dialog-description"
              data-testid={`${dataTestId}-description`}
              component="div"
              dangerouslySetInnerHTML={{ __html: description || "" }}
            />
          ) : (
            <DialogContentText
              id="alert-dialog-description"
              data-testid={`${dataTestId}-description`}
            >
              {description ? description : ""}
            </DialogContentText>
          )}
          {children}
        </DialogContent>
        <DialogActions data-testid={`${dataTestId}-actions`}>
          <CancelButton
            onClick={handleCancel}
            data-testid={`${dataTestId}-cancel-button`}
          >
            {t(messages.Cancel())}
          </CancelButton>
          {showConfirmButton && (
            <ConfirmButton
              data-testid={`${dataTestId}-confirm-button`}
              onClick={handleConfirm}
              buttonColor={confirmButtonColor}
              autoFocus
            >
              {t(messages.Confirm())}
            </ConfirmButton>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
}
